import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { confirmLabCrossDatasetLink, deleteLabCrossDatasetLink, getLabCrossDatasetLinks, markLabReviewedItem } from '../api/smartiLabApi';
import LabCard from '../components/LabCard';
import LabMetric from '../components/LabMetric';
import LabRiskBadge from '../components/LabRiskBadge';
import LabTable from '../components/LabTable';
import { useSmartiLabContext } from '../SmartiLabContext';

const initialManualForm = { burnInSourceLabel: '', confidence: '0.60', note: '' };

export default function CrossDatasetLinksPage() {
  const { sharedQuery } = useSmartiLabContext();
  const [data, setData] = useState({ rows: [], summary: null });
  const [busyKey, setBusyKey] = useState('');
  const [tab, setTab] = useState('ambiguous');
  const [selectedKey, setSelectedKey] = useState('');
  const [manualForm, setManualForm] = useState(initialManualForm);
  const [showReviewedOnly, setShowReviewedOnly] = useState(false);
  const [showUnresolvedOnly, setShowUnresolvedOnly] = useState(false);
  const [highImpactOnly, setHighImpactOnly] = useState(false);

  const load = useCallback(
    () => getLabCrossDatasetLinks(sharedQuery).then((payload) => setData({ rows: payload.rows || [], summary: payload.summary || null })),
    [sharedQuery]
  );

  useEffect(() => {
    load();
  }, [load]);

  const selectedRow = useMemo(
    () => (data.rows || []).find((row) => `${row.serialNumber}::${row.burnInSourceLabel || row.statusBucket}` === selectedKey) || null,
    [data.rows, selectedKey]
  );

  useEffect(() => {
    if (!selectedRow) {
      setManualForm(initialManualForm);
      return;
    }
    setManualForm({
      burnInSourceLabel: selectedRow.burnInSourceLabel || '',
      confidence: String(selectedRow.confidence ?? 0.6),
      note: selectedRow.note || '',
    });
  }, [selectedRow]);

  const handleConfirm = async (row, override = null) => {
    const key = `${row.serialNumber}-${row.burnInUploadId}-${row.burnInSourceLabel || 'manual'}`;
    setBusyKey(key);
    try {
      const payload = override || {
        serialNumber: row.serialNumber,
        burnInUploadId: row.burnInUploadId,
        burnInSourceLabel: row.burnInSourceLabel || `manual-${row.serialNumber}`,
        matchReason: row.matchReason || 'manual_confirmation',
        confidence: row.confidence,
        note: row.note || '',
      };
      await confirmLabCrossDatasetLink(payload);
      await load();
    } finally {
      setBusyKey('');
    }
  };

  const handleDelete = async (row) => {
    if (!row.confirmedLinkId) return;
    const key = `${row.serialNumber}-${row.burnInUploadId}-${row.burnInSourceLabel}`;
    setBusyKey(key);
    try {
      await deleteLabCrossDatasetLink(row.confirmedLinkId);
      await load();
    } finally {
      setBusyKey('');
    }
  };

  const handleReview = async (row, missing = false) => {
    const itemKey = missing ? row.serialNumber : `${row.serialNumber}::${row.burnInSourceLabel || row.burnInUploadId || 'candidate'}`;
    const key = `review-${itemKey}`;
    setBusyKey(key);
    try {
      await markLabReviewedItem({
        itemType: missing ? 'link_missing' : 'link',
        itemKey,
        label: missing ? `Missing link ${row.serialNumber}` : `Link ${row.serialNumber}`,
        note: missing ? 'Reviewed and accepted as genuinely missing.' : 'Reviewed for downstream trust.',
      });
      await load();
    } finally {
      setBusyKey('');
    }
  };

  const handleBulkReviewConfirmed = async () => {
    const rowsToReview = (data.rows || []).filter((row) => row.statusBucket === 'confirmed' && !row.reviewed && !row.highImpact);
    for (const row of rowsToReview) {
      // sequential on purpose to reuse the existing persistence path safely
      // eslint-disable-next-line no-await-in-loop
      await markLabReviewedItem({
        itemType: 'link',
        itemKey: `${row.serialNumber}::${row.burnInSourceLabel || row.burnInUploadId || 'candidate'}`,
        label: `Link ${row.serialNumber}`,
        note: 'Bulk reviewed low-risk confirmed link.',
      });
    }
    await load();
  };

  const filteredRows = (data.rows || []).filter((row) => {
    const inTab = tab === 'confirmed'
      ? row.statusBucket === 'confirmed'
      : tab === 'missing'
      ? row.statusBucket === 'missing'
      : row.statusBucket === 'candidate' || row.statusBucket === 'ambiguous';
    if (!inTab) return false;
    if (showReviewedOnly && !row.reviewed) return false;
    if (showUnresolvedOnly && (row.reviewed || row.statusBucket === 'confirmed')) return false;
    if (highImpactOnly && !row.highImpact) return false;
    return true;
  });

  return (
    <div className="lab-page">
      <LabCard
        title="Link-quality workbench"
        description="Review confirmed, ambiguous, and missing production-to-burn-in links with reviewer state, manual resolution, and downstream trust in mind."
        actions={<button type="button" className="lab-button" onClick={handleBulkReviewConfirmed}>Bulk review low-risk confirmed</button>}
      >
        <div className="lab-metric-grid">
          <LabMetric label="Candidates" value={data?.summary?.totalCandidates ?? 0} />
          <LabMetric label="Confirmed" value={data?.summary?.confirmedCount ?? 0} />
          <LabMetric label="Ambiguous" value={data?.summary?.ambiguousCount ?? 0} />
          <LabMetric label="Missing" value={data?.summary?.missingCount ?? 0} />
        </div>

        <div className="lab-segmented-tabs">
          <button type="button" className="lab-inline-button" onClick={() => setTab('confirmed')}>Confirmed</button>
          <button type="button" className="lab-inline-button" onClick={() => setTab('ambiguous')}>Ambiguous / candidate</button>
          <button type="button" className="lab-inline-button" onClick={() => setTab('missing')}>Missing</button>
          <button type="button" className="lab-inline-button" onClick={() => setShowReviewedOnly((value) => !value)}>{showReviewedOnly ? 'All review states' : 'Reviewed only'}</button>
          <button type="button" className="lab-inline-button" onClick={() => setShowUnresolvedOnly((value) => !value)}>{showUnresolvedOnly ? 'All resolutions' : 'Unresolved only'}</button>
          <button type="button" className="lab-inline-button" onClick={() => setHighImpactOnly((value) => !value)}>{highImpactOnly ? 'All impact levels' : 'High impact only'}</button>
        </div>

        <LabTable
          rows={filteredRows}
          emptyTitle="No links in this bucket."
          emptyDescription="Switch filters or widen the date scope to inspect more production-to-burn-in link evidence."
          columns={[
            { key: 'serialNumber', label: 'Production serial', render: (value, row) => <button type="button" className="lab-inline-button" onClick={() => setSelectedKey(`${row.serialNumber}::${row.burnInSourceLabel || row.statusBucket}`)}>{value}</button> },
            { key: 'productionTests', label: 'Prod sessions' },
            { key: 'latestProductionStatus', label: 'Prod status' },
            { key: 'burnInSourceLabel', label: 'Burn-in candidate', render: (value, row) => value || (row.statusBucket === 'missing' ? 'No candidate found yet' : '—') },
            { key: 'confidence', label: 'Match confidence', render: (value) => Number(value || 0).toFixed(2) },
            { key: 'matchReason', label: 'Reason / evidence' },
            { key: 'statusBucket', label: 'Current status', render: (value) => value === 'confirmed' ? <LabRiskBadge label="confirmed" /> : value === 'missing' ? <LabRiskBadge label="danger" /> : <LabRiskBadge label="watch" /> },
            { key: 'reviewedAt', label: 'Reviewed', render: (value, row) => value ? `${row.reviewedBy || 'reviewer'} • ${value}` : '—' },
          ]}
        />
      </LabCard>

      <LabCard title="Resolution panel" description="Use this panel to manually resolve ambiguous links, mark genuinely missing traceability, or remove confirmations when the evidence changes.">
        {!selectedRow ? (
          <p className="lab-helper-text">Select a row from the table to review its evidence and choose the next action.</p>
        ) : (
          <div className="lab-grid two-col">
            <div className="lab-case-grid">
              <article className="lab-compare-card">
                <strong>Evidence summary</strong>
                <p>Production serial: {selectedRow.serialNumber}</p>
                <p>Production sessions: {selectedRow.productionTests}</p>
                <p>Burn-in candidate: {selectedRow.burnInSourceLabel || 'No candidate found'}</p>
                <p>Reason: {selectedRow.matchReason || '—'}</p>
                <p>Confidence: {Number(selectedRow.confidence || 0).toFixed(2)}</p>
                <p>Reviewer: {selectedRow.confirmedByUsername || selectedRow.reviewedBy || '—'}</p>
                <p>Review timestamp: {selectedRow.confirmedAt || selectedRow.reviewedAt || '—'}</p>
              </article>
            </div>

            <div className="lab-form-grid">
              <input className="lab-input" placeholder="Burn-in source label" value={manualForm.burnInSourceLabel} onChange={(event) => setManualForm((current) => ({ ...current, burnInSourceLabel: event.target.value }))} />
              <input className="lab-input" placeholder="Confidence 0-1" value={manualForm.confidence} onChange={(event) => setManualForm((current) => ({ ...current, confidence: event.target.value }))} />
              <textarea className="lab-input" placeholder="Reviewer note / why this is trusted" value={manualForm.note} onChange={(event) => setManualForm((current) => ({ ...current, note: event.target.value }))} />
              <button
                type="button"
                className="lab-button"
                disabled={!manualForm.burnInSourceLabel || Boolean(busyKey)}
                onClick={() => handleConfirm(selectedRow, {
                  serialNumber: selectedRow.serialNumber,
                  burnInUploadId: selectedRow.burnInUploadId,
                  burnInSourceLabel: manualForm.burnInSourceLabel.trim(),
                  matchReason: selectedRow.matchReason === 'missing_link' ? 'manual_missing_resolution' : `manual_${selectedRow.matchReason || 'confirmation'}`,
                  confidence: Number(manualForm.confidence || 0),
                  note: manualForm.note,
                })}
              >
                Save manual resolution
              </button>
              <button type="button" className="lab-inline-button" disabled={Boolean(busyKey)} onClick={() => handleReview(selectedRow, selectedRow.statusBucket === 'missing')}>
                {selectedRow.statusBucket === 'missing' ? 'Mark as genuinely missing' : 'Mark reviewed'}
              </button>
              {selectedRow.confirmedLinkId ? (
                <button type="button" className="lab-inline-button danger" disabled={Boolean(busyKey)} onClick={() => handleDelete(selectedRow)}>
                  Remove confirmation
                </button>
              ) : null}
            </div>
          </div>
        )}
      </LabCard>
    </div>
  );
}
